import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { requestModelOpen } from "@/lib/activity";

// ── Tutorial steps ───────────────────────────────────────────────────────────
export const STEPS = [
  {
    id: "welcome",
    type: "modal",
    title: "Welcome to Nox AI",
    desc: "Let's take 60 seconds to show you around — then we'll guide you to generate your first AI image using one of your free signup credits.",
    cta: "Start the tour",
    skip: "Skip tour",
  },
  {
    id: "tabs",
    selector: '[data-tutorial="tabs-list"]',
    pos: "bottom",
    badge: "1 / 7",
    title: "Your AI creative studio",
    desc: "These 8 tabs unlock every AI tool on the platform — Images, Video, Audio, Stories, Storyboard, Avatars, Ad Studio, and Uncensored. Each one is just a click away.",
    cta: "Next",
  },
  {
    id: "image-tab",
    selector: '[data-tutorial="tab-image"]',
    pos: "bottom",
    badge: "2 / 7",
    title: "Start with AI Images",
    desc: "10+ state-of-the-art image models — from FLUX Schnell for fast drafts to Ideogram v3 for crisp text. Let's open the studio and explore.",
    cta: "Open Image Studio",
    action: "open-model",
  },
  {
    id: "prompt",
    selector: '[data-testid="image-prompt-input"]',
    pos: "right",
    badge: "3 / 7",
    title: "Describe your image",
    desc: "Type your idea here. The more specific, the better — \"a glowing Nike sneaker on dark marble with dramatic studio lighting\" beats \"a shoe\".",
    cta: "Got it",
  },
  {
    id: "enhancer",
    selector: '[data-testid="image-enhance-button"]',
    pos: "right",
    badge: "4 / 7",
    title: "Prompt Enhancer",
    desc: "Tap this to have our AI rewrite your prompt using professional photography language — dramatically improves output quality without extra effort.",
    cta: "Got it",
  },
  {
    id: "aspect-ratio",
    selector: '[data-testid="image-size-select"]',
    pos: "right",
    badge: "5 / 7",
    title: "Aspect ratio",
    desc: "Choose your output format — 1:1 for Instagram posts, 9:16 for Reels, 16:9 for YouTube thumbnails, or any custom ratio.",
    cta: "Got it",
  },
  {
    id: "reference",
    selector: '[data-testid="style-reference-section"]',
    pos: "right",
    badge: "6 / 7",
    title: "Reference image",
    desc: "Upload any image to lock in a visual style, color palette or composition. Perfect for keeping branded content consistent across campaigns.",
    cta: "Got it",
  },
  {
    id: "count",
    selector: '[data-testid="image-count-select"]',
    pos: "right",
    badge: "7 / 7",
    title: "Images per prompt",
    desc: "Generate 1–4 variations at once. More variations means more options to choose from — without having to re-type your prompt.",
    cta: "Got it",
  },
  {
    id: "generate",
    selector: '[data-testid="generate-image-button"]',
    pos: "top",
    badge: "Generate",
    title: "Generate your first image — free!",
    desc: "We've pre-filled a sample prompt for you. Hit Generate — it uses just 1 credit from your free signup balance. Watch the magic happen.",
    cta: "Generate now!",
    action: "trigger-generate",
    waitFor: '[data-testid="image-results"]',
    waitTitle: "Creating your image…",
    waitDesc: "Flux Schnell is generating your image. This usually takes 3–8 seconds — watch the preview panel on the right.",
    highlight: true,
  },
  {
    id: "gallery-nav",
    selector: '[data-testid="nav-gallery"]',
    pos: "bottom",
    badge: "Almost done",
    title: "Your creation is saved here",
    desc: "Every image, video and voiceover you generate is automatically saved to your Gallery — download, edit, score or share anytime.",
    cta: "See my image",
    action: "navigate-gallery",
  },
  {
    id: "done",
    type: "modal",
    title: "You're all set!",
    desc: "That's Nox AI in a nutshell. Explore all 10+ image models, try video generation, voiceover, music and stories. Welcome to the studio.",
    cta: "Start creating",
    action: "finish",
  },
];

const TUTORIAL_PROMPT =
  "a glowing neon sneaker on a dark studio backdrop, dramatic cinematic lighting, product photography, 4k sharp detail";

// ── Storage helpers ───────────────────────────────────────────────────────────
const PENDING_KEY = "nox_tutorial_pending";
const COMPLETE_KEY = "nox_tutorial_complete";

export function markTutorialPending() {
  try { localStorage.setItem(PENDING_KEY, "true"); } catch {}
}
export function isTutorialPending() {
  try { return localStorage.getItem(PENDING_KEY) === "true"; } catch { return false; }
}
export function markTutorialComplete() {
  try {
    localStorage.removeItem(PENDING_KEY);
    localStorage.setItem(COMPLETE_KEY, "true");
  } catch {}
}

// ── Context ───────────────────────────────────────────────────────────────────
const TutorialCtx = createContext(null);

export function TutorialProvider({ children }) {
  const [step, setStep] = useState(-1); // -1 = inactive
  const [waiting, setWaiting] = useState(false); // waiting for image-results after generate
  const waitTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Start tutorial when navigating to /studio with pending flag
  useEffect(() => {
    if (location.pathname === "/studio" && isTutorialPending()) {
      const t = setTimeout(() => setStep(0), 600);
      return () => clearTimeout(t);
    }
  }, [location.pathname]);

  // 60-second safety-net: if image never arrives, advance anyway
  useEffect(() => {
    if (!waiting) return;
    waitTimeoutRef.current = setTimeout(() => {
      setWaiting(false);
      setStep((s) => {
        const n = s + 1;
        return n >= STEPS.length ? (markTutorialComplete(), -1) : n;
      });
    }, 60000);
    return () => clearTimeout(waitTimeoutRef.current);
  }, [waiting]);

  const start = useCallback(() => setStep(0), []);

  const next = useCallback(() => {
    setStep((s) => {
      const n = s + 1;
      if (n >= STEPS.length) { markTutorialComplete(); return -1; }
      return n;
    });
  }, []);

  const skip = useCallback(() => {
    markTutorialComplete();
    setWaiting(false);
    setStep(-1);
  }, []);

  // Called by TutorialOverlay when waitFor element is found
  const stopWaiting = useCallback(() => {
    clearTimeout(waitTimeoutRef.current);
    setWaiting(false);
    next();
  }, [next]);

  const runAction = useCallback(
    (action) => {
      if (action === "open-model") {
        requestModelOpen({ key: "flux_schnell", mediaType: "image", prompt: TUTORIAL_PROMPT });
        next();
      } else if (action === "trigger-generate") {
        const btn = document.querySelector('[data-testid="generate-image-button"]');
        if (btn && !btn.disabled) btn.click();
        setWaiting(true); // stay on this step until image-results appears
      } else if (action === "navigate-gallery") {
        navigate("/gallery");
        next();
      } else if (action === "finish") {
        markTutorialComplete();
        setStep(-1);
      } else {
        next();
      }
    },
    [next, navigate]
  );

  return (
    <TutorialCtx.Provider value={{ step, start, next, skip, runAction, active: step >= 0, waiting, stopWaiting }}>
      {children}
    </TutorialCtx.Provider>
  );
}

export function useTutorial() {
  return useContext(TutorialCtx);
}
